/* eslint-disable no-console -- 에러 추적 시스템은 console 사용이 필수적 */
/**
 * Error Tracking System
 * 상황별, 컨텍스트별 에러 추적 및 강화 (Enrichment)
 */

import { ApiError, isApiError } from '../api/errors';

import { reportErrorToServer } from './reporter';

/**
 * 에러 컨텍스트 정보
 */
export interface ErrorContext {
  component?: string;        // 에러 발생 컴포넌트 (예: "CreatePlaylistPage")
  function?: string;         // 에러 발생 함수 (예: "handleSubmit")
  action?: string;           // 사용자 액션 (예: "submit_form", "fetch_data")
  endpoint?: string;         // API 엔드포인트 (예: "POST /api/playlists/")
  timestamp?: string;        // ISO 8601 타임스탐프
  userAgent?: string;        // 브라우저 정보
  url?: string;              // 페이지 URL
  additionalData?: Record<string, string | number | boolean | null | undefined>; // 추가 데이터
}

/**
 * 강화된 에러 객체
 */
export interface EnrichedError {
  error: ApiError | Error;
  context: ErrorContext;
  severity: 'critical' | 'error' | 'warning' | 'info';
  userFriendlyMessage: string;
  debugInfo: {
    stack?: string;
    originalError?: unknown;
    timestamp: string;
    environment: 'development' | 'production';
  };
}

/**
 * 에러 트래커 클래스
 * 싱글톤 패턴으로 에러를 수집하고 추적합니다
 */
class ErrorTracker {
  private static instance: ErrorTracker;
  private errorHistory: EnrichedError[] = [];
  private readonly maxHistorySize = 50; // 최대 50개까지만 메모리에 유지

  private constructor() {}

  static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  /**
   * 에러를 강화된 형식으로 변환
   */
  enrichError(
    error: unknown,
    context: ErrorContext,
    severity: 'critical' | 'error' | 'warning' | 'info' = 'error'
  ): EnrichedError {
    const timestamp = new Date().toISOString();
    const parsedError = this.normalizeError(error);
    const userFriendlyMessage = this.getUserFriendlyMessage(parsedError);

    const enrichedError: EnrichedError = {
      error: parsedError,
      context: {
        timestamp,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        ...context,
      },
      severity,
      userFriendlyMessage,
      debugInfo: {
        stack: error instanceof Error ? error.stack : undefined,
        originalError: error,
        timestamp,
        environment: (process.env.NODE_ENV || 'development') as 'development' | 'production',
      },
    };

    // 히스토리에 추가
    this.addToHistory(enrichedError);

    return enrichedError;
  }

  /**
   * 에러를 정규화 (ApiError 또는 일반 Error로 변환)
   */
  private normalizeError(error: unknown): ApiError | Error {
    if (isApiError(error)) {
      return error;
    }
    if (error instanceof Error) {
      return error;
    }
    return new Error(typeof error === 'string' ? error : JSON.stringify(error));
  }

  /**
   * 사용자 친화적 메시지 생성
   */
  private getUserFriendlyMessage(error: ApiError | Error): string {
    if (isApiError(error)) {
      return error.getUserMessage();
    }

    const message = error.message;
    if (message.includes('network') || message.includes('Network')) {
      return '네트워크 연결을 확인해주세요.';
    }
    if (message.includes('timeout') || message.includes('Timeout')) {
      return '요청 시간이 초과되었습니다. 다시 시도해주세요.';
    }
    return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  }

  /**
   * 히스토리에 에러 추가
   */
  private addToHistory(enrichedError: EnrichedError): void {
    this.errorHistory.push(enrichedError);

    // 최대 크기 초과 시 오래된 항목 제거
    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory.shift();
    }
  }

  /**
   * 에러 히스토리 조회
   */
  getErrorHistory(): EnrichedError[] {
    return [...this.errorHistory];
  }

  /**
   * 특정 컴포넌트의 에러 히스토리 조회
   */
  getErrorsByComponent(component: string): EnrichedError[] {
    return this.errorHistory.filter((e) => e.context.component === component);
  }

  /**
   * 특정 심각도 이상의 에러 조회
   */
  getErrorsBySeverity(severity: 'critical' | 'error' | 'warning' | 'info'): EnrichedError[] {
    const severityLevel = { critical: 4, error: 3, warning: 2, info: 1 };
    return this.errorHistory.filter((e) => severityLevel[e.severity] >= severityLevel[severity]);
  }

  /**
   * 최근 N개의 에러 조회
   */
  getRecentErrors(count: number = 10): EnrichedError[] {
    return this.errorHistory.slice(-count);
  }

  /**
   * 에러 히스토리 초기화
   */
  clearHistory(): void {
    this.errorHistory = [];
  }

  /**
   * 에러를 상세 로깅
   */
  logDetailedError(enrichedError: EnrichedError): void {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    console.group(`%c⚠️ DETAILED ERROR REPORT`, 'color: red; font-weight: bold; font-size: 14px');

    // 기본 정보
    console.log('%cError Message:', 'color: #ff6b6b; font-weight: bold');
    console.log(enrichedError.userFriendlyMessage);

    // API 에러 상세 정보
    if (isApiError(enrichedError.error)) {
      const apiError = enrichedError.error as ApiError;
      console.log('%cAPI Error Details:', 'color: #ff8c42; font-weight: bold');
      console.table({
        'HTTP Status': apiError.statusCode,
        'Error Code': apiError.errorCode,
        'Is Validation Error': apiError.isValidationError,
        'Is Auth Error': apiError.isAuthError,
        'Is Network Error': apiError.isNetworkError,
      });

      if (apiError.details && Object.keys(apiError.details).length > 0) {
        console.log('%cValidation Details:', 'color: #ffd43b; font-weight: bold');
        console.table(apiError.details);
      }
    }

    // 컨텍스트 정보
    console.log('%cContext Information:', 'color: #4c6ef5; font-weight: bold');
    console.table({
      Component: enrichedError.context.component || 'N/A',
      Function: enrichedError.context.function || 'N/A',
      Action: enrichedError.context.action || 'N/A',
      Endpoint: enrichedError.context.endpoint || 'N/A',
      Timestamp: enrichedError.context.timestamp,
    });

    // 페이지/환경 정보
    console.log('%cEnvironment:', 'color: #a78bfa; font-weight: bold');
    console.table({
      'Page URL': enrichedError.context.url,
      Environment: enrichedError.debugInfo.environment,
      'User Agent': enrichedError.context.userAgent,
      Severity: enrichedError.severity,
    });

    // 추가 데이터
    if (enrichedError.context.additionalData && Object.keys(enrichedError.context.additionalData).length > 0) {
      console.log('%cAdditional Data:', 'color: #69db7c; font-weight: bold');
      console.log(enrichedError.context.additionalData);
    }

    // 스택 트레이스
    if (enrichedError.debugInfo.stack) {
      console.log('%cStack Trace:', 'color: #ff922b; font-weight: bold');
      console.log(enrichedError.debugInfo.stack);
    }

    console.groupEnd();
  }

  /**
   * 에러를 JSON으로 내보내기 (디버깅용)
   */
  exportErrorAsJSON(enrichedError: EnrichedError): string {
    return JSON.stringify(
      {
        message: enrichedError.userFriendlyMessage,
        errorMessage: enrichedError.error.message,
        context: enrichedError.context,
        severity: enrichedError.severity,
        timestamp: enrichedError.debugInfo.timestamp,
        ...(isApiError(enrichedError.error) && {
          statusCode: enrichedError.error.statusCode,
          errorCode: enrichedError.error.errorCode,
          details: enrichedError.error.details,
        }),
      },
      null,
      2
    );
  }
}

// 싱글톤 인스턴스 내보내기
export const errorTracker = ErrorTracker.getInstance();

/**
 * 편의 함수: 에러를 추적하고 상세 로깅하기
 */
export function trackAndLogError(
  error: unknown,
  context: ErrorContext,
  severity: 'critical' | 'error' | 'warning' | 'info' = 'error'
): EnrichedError {
  const enrichedError = errorTracker.enrichError(error, context, severity);
  errorTracker.logDetailedError(enrichedError);
  // 에러 인박스(백엔드)로 비동기 전송. 실패해도 흐름에 영향 없음.
  void reportErrorToServer(enrichedError);
  return enrichedError;
}

/**
 * 편의 함수: 컴포넌트에서 쉽게 사용할 수 있는 에러 추적
 */
export function createErrorTracker(component: string) {
  return {
    track: (error: unknown, context?: Partial<ErrorContext>) =>
      errorTracker.enrichError(error, { component, ...context }),
    logDetailed: (error: unknown, context?: Partial<ErrorContext>) => {
      const enriched = errorTracker.enrichError(error, { component, ...context });
      errorTracker.logDetailedError(enriched);
      return enriched;
    },
  };
}
